/* **A personal record's card** (`docs/design/zadania-progi-2026-10-02/`, view 4) — the movement's
 * picture, the words beside it, and on the desk the session's lines and four facts under them.
 *
 * The anatomy is the assigned plan's Przegląd header: no frame, the photograph enters from the
 * card's right edge under the head's rule and dissolves into the ground before the words and before
 * what stands below (`shared/ui/photoFade`), so every word sits on the card's own ground. A FIXED
 * height, so the wait, a photograph and none are the same box.
 *
 * Lime stands on the kind's mark, the record sets and their figure — nothing else; the movement's
 * name is the page's ink. */

.hero {
  --record-focus: 50% 40%;

  position: relative;
  flex: none;
  height: var(--h-record-hero);
}

/* The desk card's body is padded 16 above and 24 beside; the picture bleeds through both to the
 * card's own edge, and as far down into the gap. */
.photo {
  position: absolute;
  top: calc(-1 * var(--space-4));
  right: calc(-1 * var(--space-6));
  width: calc(56% + var(--space-6));
  height: calc(100% + var(--space-4) + var(--space-4));
  object-position: var(--record-focus);
}

/* The words end before the photograph begins. Without one they take the card's measure and the box
 * keeps its height — the empty ground reads as layout, not as a missing picture. */
.text {
  position: absolute;
  top: var(--space-0);
  bottom: var(--space-0);
  left: var(--space-0);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  max-width: 620px;
}

.onPhoto .text {
  width: calc(46% - var(--space-2));
  max-width: none;
}

.words {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.title {
  display: -webkit-box;
  overflow: hidden;
  font-size: var(--fs-overview-verdict);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-plan-hero);
  letter-spacing: var(--ls-heading);
  color: var(--text);
  text-wrap: balance;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.sub {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* The result holds its line from the first frame: the session read fills it in. */
.value {
  min-height: round(up, calc(1em * var(--lh-normal)), 1px);
  margin-top: var(--space-1);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-extrabold);
  font-variant-numeric: tabular-nums;
  line-height: var(--lh-normal);
  color: var(--text);
}

.previous {
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* ── The Przegląd's card: the same picture inside the body's own box ─────────────────────────
 * A rounded block (the plan card's cover radius) feathered on its leading edge only, the words
 * centred beside it. */

/* The body's stack, stretched to the card's fixed body so the hero fills it. */
.quickBody {
  flex: 1;
  min-height: 0;
}

.quick {
  flex: 1;
  height: auto;
  min-height: 0;
}

.quick .photo {
  top: var(--space-0);
  right: var(--space-0);
  width: 52%;
  height: 100%;
  border-radius: var(--radius-md);
}

.quick .text {
  justify-content: center;
}

.quick .title {
  font-size: var(--fs-stat);
}

/* ── Under the hero, on the desk ───────────────────────────────────────────────────────────── */

.detailHead {
  display: flex;
  gap: var(--space-4);
  align-items: baseline;
  justify-content: space-between;
}

.legend {
  display: flex;
  gap: var(--space-3);
  font-size: var(--fs-caption);
  color: var(--text-2);
}

.legendItem {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
}

/* The lines and the tiles arrive together. */
.detail {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

/* The detail's box stands from the first frame — before the card is on screen, through the wait and
 * under the answer — as tall as the SHORTEST answer: one line for each movement the item says holds
 * a record (three at most are drawn), and the tiles. Nothing stands under this box, so a session
 * with more to say grows at the card's foot and moves nothing — where a fixed three-line reserve
 * left a one-record session a third of the card's height of blank ground. */
.detailBox {
  --record-detail: calc(var(--record-lines) * var(--h-record-line) + var(--space-3) + var(--h-record-tiles));

  min-height: var(--record-detail);
}

.reserveDetail { height: var(--record-detail); }

.lines1 { --record-lines: 1; }
.lines2 { --record-lines: 2; }
.lines3 { --record-lines: 3; }

/* The tiles at the height the reserve counts them at. */
.tiles { height: var(--h-record-tiles); }
